// main.js — application entry point for tracko-web.
// Purpose: install the Vuetify plugin (side effect), wire router + store + i18n,
// register the Tier-1 engines' core modules, then create and mount the root Vue
// instance at #app. Vuetify 1.5 installs via Vue.use (in plugins/vuetify.js) and is
// NOT passed as a `vuetify` option to `new Vue` (that is the Vuetify 2 convention).
// Reconstructed from lacak.io deployed behavior; app-specific contract verbatim,
// internal impl re-authored.

import Vue from 'vue'
import './plugins/vuetify' // Vue.use(Vuetify, {...}) — must run before the app mounts
import App from './App.vue'
import router from './router'
import store from './store'
import i18n from './i18n'
import { installEngines } from './engines'
import { setRouter, setVueInstance } from './api/context.js'
import { installMock } from './mock/index.js'
import { hasSession } from './shell/session'
import tip from './directives/tip.js'
import './styles/main.scss'

Vue.config.productionTip = false

// Global `v-tip` directive — the styled hover tooltip (matches go.lacak.io's popper tooltip:
// black 70% box, white 14px, arrow, short delay). Portalled to <body> so panels can't clip it.
Vue.directive('tip', tip)

// Dev-only offline demo backend (VITE_MOCK=1). Installs an axios mock adapter and
// forces the realtime engine onto the poller so the app runs with dummy data and
// no live api.lacak.io session. No-op in a normal build.
if (import.meta.env.VITE_MOCK === '1') {
  installMock()
}

// Populate the app-context registry the request layer reads through (getStore is
// already set by src/store/index.js). This lets the status-code error table push
// '/server-error' (code 12) and force-logout via the router. (AUTH-SESSION §2.3)
setRouter(router)

async function bootstrap() {
  // Register the auth engine's core modules (user/paasUser) before first render so
  // the guard, top bar and login form can dispatch `user/*` immediately. Guarded
  // so a not-yet-built engine can't block boot.
  try {
    await installEngines(store)
  } catch (err) {
    // eslint-disable-next-line no-console
    console.warn('[tracko] engine bootstrap issue:', err)
  }

  const app = new Vue({
    router,
    store,
    i18n,
    render: (h) => h(App)
  }).$mount('#app')

  // Expose the root instance to the request layer's getVue() indirection.
  setVueInstance(app)

  // Boot-time session hydration: when a session cookie already exists (page reload,
  // not a fresh login), the deployed app re-fetches user/get_info so the user block,
  // permissions and settings are populated. loadUser also opens the 60s get_info poll.
  if (hasSession()) {
    store.dispatch('user/loadUser').catch(() => { /* auto-logout handled by request layer */ })
  }
}

bootstrap()
